import { namedFuzzy, searchPrintsByNameExact, fetchScryfallCardFullById } from "@/components/scan/CardScanner";
import type { TradeItem } from "./tradeTypes";

export function formatCurrency(n: number | null): string {
  return n == null ? "$0.00" : n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function computeTotal(items: TradeItem[]): number {
  return items.reduce((sum, it) => sum + it.qty * (it.unitPrice ?? 0), 0);
}

export function priceFromScryfall(sf: any, foil: boolean | undefined): number | null {
  const p = foil ? sf?.prices?.usd_foil : sf?.prices?.usd;
  const f = parseFloat(p || "");
  return Number.isFinite(f) ? f : null;
}

export function toTradeItemFromScryfall(sf: any): TradeItem {
  const image =
    sf?.image_uris?.normal ||
    sf?.image_uris?.large ||
    (Array.isArray(sf?.card_faces) ? sf.card_faces[0]?.image_uris?.normal : null) ||
    null;

  const key = `${sf?.id || cryptoRandom()}-${Date.now()}`;
  const setCode = (sf?.set || "").toUpperCase() || null;
  const number = (sf?.collector_number || "").toString() || null;

  return {
    key,
    origin: "scryfall",
    ownerUid: null,
    docId: null,
    scryfall_id: sf?.id || null,
    name: sf?.name || "Unknown",
    set: setCode,
    number,
    image,
    foil: Boolean(sf?.foil),
    qty: 1,
    unitPrice: priceFromScryfall(sf, Boolean(sf?.foil)),
    meta: { set_name: sf?.set_name ?? null },
  };
}

export async function searchScryfallSimple(query: string): Promise<any[]> {
  const q = query.trim();
  if (!q) return [];
  // Prefer exact prints when the query seems precise, else try fuzzy fallback.
  try {
    const prints = await searchPrintsByNameExact(q);
    if (Array.isArray(prints) && prints.length > 0) return prints.slice(0, 12);
  } catch {}
  try {
    const fuzzy = await namedFuzzy(q);
    return fuzzy ? [fuzzy] : [];
  } catch {}
  return [];
}

export async function fillMissingPricesWithScryfall(items: TradeItem[]): Promise<TradeItem[]> {
  return Promise.all(
    items.map(async (it) => {
      if (it.unitPrice != null) return it;
      if (!it.scryfall_id) return it;
      try {
        const sf = await fetchScryfallCardFullById(it.scryfall_id);
        return { ...it, unitPrice: priceFromScryfall(sf, it.foil) };
      } catch {
        return it;
      }
    }),
  );
}

export function toTradeItemFromUserCard(
  c: {
    id: string;
    scryfall_id?: string | null;
    name: string;
    set?: string | null;
    number?: string | null;
    image?: string | null;
    foil?: boolean;
    qty?: number | null;
    price?: number | null;
    condition?: string | null;
  },
  ownerUid: string,
): TradeItem {
  return {
    key: `${c.id}-${Date.now()}`,
    origin: "my-collection",
    ownerUid,
    docId: c.id,
    scryfall_id: c.scryfall_id ?? null,
    name: c.name,
    set: c.set ?? null,
    number: c.number ?? null,
    image: c.image ?? null,
    foil: Boolean(c.foil),
    qty: 1,
    unitPrice: typeof c.price === "number" ? c.price : null,
    meta: { condition: c.condition ?? null, maxQty: Math.max(1, Number(c.qty || 1)) },
  };
}

export function toTradeItemFromOtherUserCard(
  c: {
    id: string;
    scryfall_id?: string | null;
    name: string;
    set?: string | null;
    number?: string | null;
    image?: string | null;
    foil?: boolean;
    qty?: number | null;
    price?: number | null;
    condition?: string | null;
  },
  ownerUid: string,
): TradeItem {
  return {
    key: `${c.id}-${Date.now()}`,
    origin: "other-collection",
    ownerUid,
    docId: c.id,
    scryfall_id: c.scryfall_id ?? null,
    name: c.name,
    set: c.set ?? null,
    number: c.number ?? null,
    image: c.image ?? null,
    foil: Boolean(c.foil),
    qty: 1,
    unitPrice: typeof c.price === "number" ? c.price : null,
    meta: { condition: c.condition ?? null, maxQty: Math.max(1, Number(c.qty || 1)) },
  };
}

/**
 * Merge new items into existing by increasing qty on identical lines.
 * Identity priority:
 * 1) Same docId (same collection line)
 * 2) Else same (scryfall_id, set, number, foil, unitPrice, origin)
 */
export function mergeTradeItems(existing: TradeItem[], additions: TradeItem[]): TradeItem[] {
  const out = [...existing];
  for (const add of additions) {
    const idx = out.findIndex((e) => {
      if (add.docId && e.docId && add.docId === e.docId) return true;
      return (
        (add.scryfall_id || null) === (e.scryfall_id || null) &&
        (add.set || null) === (e.set || null) &&
        (add.number || null) === (e.number || null) &&
        Boolean(add.foil) === Boolean(e.foil) &&
        (add.unitPrice ?? null) === (e.unitPrice ?? null) &&
        add.origin === e.origin
      );
    });
    if (idx >= 0) {
      const merged = { ...out[idx], qty: out[idx].qty + Math.max(1, Math.floor(add.qty || 1)) };
      out[idx] = merged;
    } else {
      out.push(add);
    }
  }
  return out;
}

function cryptoRandom(): string {
  try {
    const a = new Uint32Array(2);
    crypto.getRandomValues(a);
    return `${a[0].toString(16)}${a[1].toString(16)}`;
  } catch {
    return Math.random().toString(16).slice(2);
  }
}
